// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { AppProfile } from '@api/types';
import { formatDateInUtcWithTz } from '@utils/utils';
import type { TableColumnsType } from 'antd';
import type { TableRowSelection } from 'antd/lib/table/interface';
import type { useTranslation } from 'react-i18next';
import CopyButton from '../../../../components/copyButton/copyButton';
import { TRANSLATION_ROOT } from './AppProfiles.types';

type Translate = ReturnType<typeof useTranslation>['t'];

const renderCopyableText = (value: string, t: Translate) => (
  <span className="app-profile-cell">
    <span className="app-profile-cell-text">{value}</span>
    <CopyButton textToCopy={value} aria-label={t(`${TRANSLATION_ROOT}.copy-value`, { value })} />
  </span>
);

export function buildAppProfilesColumns(t: Translate): TableColumnsType<AppProfile> {
  return [
    {
      title: t(`${TRANSLATION_ROOT}.columns.display-name`),
      dataIndex: 'displayName',
      key: 'displayName',
      ellipsis: { showTitle: true },
      render: (displayName: string) => renderCopyableText(displayName, t),
    },
    {
      title: t(`${TRANSLATION_ROOT}.columns.machine-name`),
      dataIndex: 'machineName',
      key: 'machineName',
      ellipsis: { showTitle: true },
      render: (machineName: string) => renderCopyableText(machineName, t),
    },
    {
      title: t(`${TRANSLATION_ROOT}.columns.created-at`),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => formatDateInUtcWithTz(createdAt),
    },
    {
      title: t(`${TRANSLATION_ROOT}.columns.updated-at`),
      key: 'updatedAt',
      render: (_: unknown, profile: AppProfile) =>
        formatDateInUtcWithTz(profile.updatedAt || profile.createdAt),
    },
    {
      title: t(`${TRANSLATION_ROOT}.columns.id`),
      dataIndex: 'appProfileId',
      key: 'appProfileId',
      width: 160,
      ellipsis: { showTitle: true },
      render: (appProfileId: string) => renderCopyableText(appProfileId, t),
    },
  ];
}

export function buildAppProfilesRowSelection(
  selectedAppProfileIds: string[],
  onSelectionChange: (appProfileIds: string[]) => void,
): TableRowSelection<AppProfile> {
  return {
    selectedRowKeys: selectedAppProfileIds,
    onChange: selectedRowKeys =>
      onSelectionChange(selectedRowKeys.filter((k): k is string => typeof k === 'string')),
  };
}
